<script setup lang="ts">
import { DialogDescription, DialogTitle } from 'reka-ui';
import { computed, watch } from 'vue';

import DocsLink from '@/components/DocsLink.vue';
import { KaButton, KaCopyLine, KaModal } from '@/kasten-components';
import SvgCluster from '@/pages/mc/SvgCluster.vue';
import { useDrRestoreStore } from '@/stores/disasterRecovery/drRestoreStore';

const props = defineProps<{
  open: boolean;
}>();

const drRestoreStore = useDrRestoreStore();

const isRunning = computed(
  () => drRestoreStore.drRestoreStatus.drRestoreStatusResponse?.data.status === 'running',
);
const isSuccess = computed(
  () => drRestoreStore.drRestoreStatus.drRestoreStatusResponse?.data.status === 'success',
);
const isNone = computed(
  () => drRestoreStore.drRestoreStatus.drRestoreStatusResponse?.data.status === 'none',
);
const isCompleted = computed(() => drRestoreStore.drRestoreStatus.isCompletedRestoreModalShowing);

watch(
  () => isCompleted.value,
  () => {
    document.getElementById('docsLink')?.focus();
  },
);
</script>

<template>
  <KaModal
    v-if="isRunning"
    id="drRestoreModal"
    modal-classes="p-6 max-w-[596px]"
    :open="props.open"
    width="full"
  >
    <template #default>
      <div class="flex">
        <SvgCluster class="mr-6 size-16 text-blue-400 dark:text-cyan-500" />
        <div class="my-auto flex flex-col items-start">
          <DialogTitle tabindex="0" class="text-lg font-bold focus:outline-none"
            >Restore in progress...</DialogTitle
          >

          <DialogDescription aria-live="assertive" class="text-base">{{
            drRestoreStore.drRestoreStatus.drRestoreStatusResponse?.data?.progress
          }}</DialogDescription>
        </div>
      </div>
    </template>
  </KaModal>

  <KaModal
    v-else-if="isSuccess || isNone"
    id="drRestoreCompletedModal"
    modal-classes="p-6 max-w-[596px]"
    :open="props.open"
    width="full"
  >
    <template #default>
      <div class="flex flex-col">
        <DialogTitle as="h2" class="flex text-lg font-semibold leading-6"
          >Disaster Recovery Completed</DialogTitle
        >
        <DialogDescription
          role="alert"
          aria-live="assertive"
          class="text-sm text-gray-600 dark:text-gray-400"
        >
          If the restored Kasten instance has replaced the original instance, run the following
          command to resume background maintenance operations on backup data.
          <br />
          <DocsLink
            ref="docsLink"
            id="docsLink"
            path="operating/dr#using-the-restored-k10-in-place-of-the-original"
          >
            See Documentation
          </DocsLink>
        </DialogDescription>

        <KaCopyLine
          id="deleteConfigMapCommand"
          command="kubectl delete configmap --namespace=kasten-io k10-dr-remove-to-get-ownership"
        />

        <KaButton variant="primary" class="mr-auto mt-6" @click="drRestoreStore.resetStore">
          close
        </KaButton>
      </div>
    </template>
  </KaModal>
</template>
